/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Row,
  Col,
  FormGroup,
  Button,
  Container,
  Card,
  CardBody,
  UncontrolledTooltip,
  Label,
  CustomInput,
  Collapse,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { Form } from '@unform/web';
import { Scope } from '@unform/core';
import { ValidationError } from 'yup';
import { toast } from 'react-toastify';
import IntlCurrencyInput from 'react-intl-currency-input';
import api from '~/services/api';
import history from '~/services/history';
import { getCitiesByUf } from '~/util/ibge';
import { cpfSchema } from '~/validators/form/new_pix_link';
import { isDate, parse, isAfter } from 'date-fns';

import Input from '~/components/Inputs/Input';
import WalletSettingsBlock from '~/pages/Stores/WalletSettingsBlock';
import InputMask from '~/components/Inputs/InputMask';
import InputSelect from '~/components/Inputs/InputSelect';
import StoreLocationPicker from '~/components/StoreLocationPicker';
import SchemaValidator from '~/validators/form/add_store_form';
import Loading from '~/components/Loading';
import PageHeader from '~/components/Common/PageHeader';
import IconButton from '~/components/IconButton';
import DeliveryOperatorsList from '~/components/DeliveryOperatorsList';
import {
  bnplGatewayOptions,
  externalGatewayOptions,
  gatewayOptions,
} from '~/util/filters';
import { stateList } from '~/util/stateList';
import ImageInput from '~/components/Inputs/ImageInput';
import ColorPicker from '~/components/ColorPicker';
// import { copyStringToClipboard } from '~/util/copyToClipboard';

const visuallyHiddenStyle = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const SectionCard = ({
  title,
  subtitle,
  summary,
  collapsible = false,
  isOpen = true,
  onToggle,
  children,
}) => {
  const contentIsOpen = collapsible ? isOpen : true;

  return (
    <Card className="new-layout card mb-4">
      <CardBody className="new-layout card-body px-3 py-3">
        <fieldset className="border-0 p-0 m-0">
          <legend style={visuallyHiddenStyle}>{title}</legend>

          <Row
            className={`w-100 mx-0 mb-lg-2 ${
              collapsible ? 'cursor-pointer' : ''
            }`}
            onClick={collapsible ? onToggle : undefined}
          >
            <Col className="font-size-16 font-weight-600 px-0">
              <Row className="w-100 mx-0 d-flex justify-content-between align-items-start">
                <div className="px-0">
                  <div className="text-gray-700">{title}</div>
                  {subtitle && (
                    <div className="text-muted font-weight-400 font-size-12 mt-1">
                      {subtitle}
                    </div>
                  )}
                  {summary && (
                    <div className="text-muted font-weight-400 font-size-12 mt-1">
                      {summary}
                    </div>
                  )}
                </div>

                {collapsible && (
                  <div className="px-0">
                    <IconButton
                      icon={`las la-${
                        contentIsOpen ? 'angle-up' : 'angle-right'
                      }`}
                      click={event => {
                        event?.stopPropagation?.();
                        onToggle?.();
                      }}
                      color="gray-700"
                    />
                  </div>
                )}
              </Row>
            </Col>
          </Row>

          <Collapse isOpen={contentIsOpen}>
            <div className="pt-3">{children}</div>
          </Collapse>
        </fieldset>
      </CardBody>
    </Card>
  );
};

const SubSectionBlock = ({ title, hint, children, className = '' }) => (
  <div className={`border rounded p-3 mb-3 ${className}`}>
    <div className="font-weight-700 text-gray-700 mb-1">{title}</div>
    {hint && <div className="text-muted font-size-12 mb-3">{hint}</div>}
    {children}
  </div>
);

const EditStore = () => {
  const formRef = useRef(null);
  const certificateRef = useRef(null);
  const internalAddressUpdateRef = useRef(false);
  const [cityList, setCityList] = useState([]);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [pagarmeHub, setPagarmeHub] = useState(null);

  const [storeLocation, setStoreLocation] = useState(null);
  const [addressNumber, setAddressNumber] = useState('');
  const [pinMovedManually, setPinMovedManually] = useState(false);
  const [addressSyncState, setAddressSyncState] = useState('synced');
  const [addressGeocodeTrigger, setAddressGeocodeTrigger] = useState(0);
  const [didBootstrapMap, setDidBootstrapMap] = useState(false);

  const [showRemoveHubModal, setShowRemoveHubModal] = useState(false);

  const [viddyPayEnabled, setViddyPayEnabled] = useState(false);
  const [manualPagarmeKeys, setManualPagarmeKeys] = useState({
    publicKey: '',
    secretKey: '',
  });

  const [editingCredentials, setEditingCredentials] = useState({
    pagarme: false,
    pagarmeExternal: false,
    cieloGerencianet: false,
    cieloMerchant: false,
    redeGerencianet: false,
    rede: false,
    pagseguro: false,
    mercadopago: false,
    clearsale: false,
    crmbonus: false,
    koin: false,
  });

  const [openSections, setOpenSections] = useState({
    branding: false,
    technical: false,
    payments: false,
    integrations: false,
    deliveryRouter: false,
  });

  const [loading, setLoading] = useState(true);
  const [initialStore, setInitialStore] = useState();
  const [onlinePaymentLimit, setOnlinePaymentLimit] = useState();
  const [minimumDeliveryBalance, setMinimumDeliveryBalance] = useState();
  const [defaultFreight, setDefaultFreight] = useState();
  const [minimumValueForFreeDelivery, setMinimumValueForFreeDelivery] =
    useState();
  const [installmentLimitAmount, setInstallmentLimitAmount] = useState();
  const [maximumDeliveryKilometer, setMaximumDeliveryKilometer] = useState();
  const [mottuUnitCode, setMottuUnitCode] = useState(null);
  const [maximumInstallments, setMaximumInstallments] = useState();

  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [justaAliasName, setJustaAliasName] = useState();
  const [justaCnae, setJustaCnae] = useState();
  const [justaConstitutionDate, setJustaConstitutionDate] = useState();
  const [justaOwnerName, setJustaOwnerName] = useState();
  const [justaOwnerCpf, setJustaOwnerCpf] = useState();
  const [justaOwnerEmail, setJustaOwnerEmail] = useState();
  const [justaOwnerCellphone, setJustaOwnerCellphone] = useState();
  const [justaOwnerPhone, setJustaOwnerPhone] = useState();
  const [justaOwnerBirthDate, setJustaOwnerBirthDate] = useState();
  const [justaOwnerRg, setJustaOwnerRg] = useState();
  const [justaAccountCpf, setJustaAccountCpf] = useState();
  const [justaAccountCode, setJustaAccountCode] = useState();
  const [justaAccountAgency, setJustaAccountAgency] = useState();
  const [justaAccountOwnerName, setJustaAccountOwnerName] = useState();
  const [justaAccountNumber, setJustaAccountNumber] = useState();

  const [deliveryRouterEnabled, setDeliveryRouterEnabled] = useState(false);
  const [deliveryRouterIncentiveStep, setDeliveryRouterIncentiveStep] =
    useState(0);
  const [deliveryOperators, setDeliveryOperators] = useState([]);
  const [vehicleLabels, setVehicleLabels] = useState({});
  const [loadingOperators, setLoadingOperators] = useState(false);

  const [color, setColor] = useState('#2544b1');
  const [logo, setLogo] = useState('');
  const [file, setFile] = useState('');

  const [selectedBnplGateway, setSelectedBnplGateway] = useState(
    bnplGatewayOptions[0]
  );
  const [selectedGateway, setSelectedGateway] = useState(gatewayOptions[0]);
  /*
  const mappLink = useSelector(
    ({ user }) => user?.profile?.subscriber?.mapp_url_webhook
  );
  */

  const [cieloMerchantId, setCieloMerchantId] = useState('');
  const [cieloMerchantKey, setCieloMerchantKey] = useState('');

  const [telephoneV, setTelephone] = useState('');
  const [financierPhone, setFinancierPhone] = useState('');

  const { store_id } = useParams();

  const [certificate, setCertificate] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState(null);

  const [noNumber, setNoNumber] = useState(false);
  const [externalGateway, setExternalGateway] = useState(false);
  const [splitPayment, setSplitPayment] = useState(false);
  const [hidePaymentData, setHidePaymentData] = useState(false);
  const [threeDS, setThreeDS] = useState(false);
  const [googlePayEnabled, setGooglePayEnabled] = useState(false);
  const [applePayEnabled, setApplePayEnabled] = useState(false);
  const [clearsaleAppName, setClearsaleAppName] = useState(false);

  const [useAutomaticFreight, setUseAutomaticFreight] = useState(false);
  const [whatsappFlowEnabled, setWhatsappFlowEnabled] = useState(false);

  const [extraPersistenceFields, setExtraPersistenceFields] = useState({
    cnpj: '',
    cep: '',
  });

  const accountTypes = useMemo(
    () => [
      {
        label: 'Poupança',
        value: 'saving',
      },
      {
        label: 'Corrente',
        value: 'checking',
      },
    ],
    []
  );

  const antifraudOptions = useMemo(
    () => [
      { label: 'Nenhum', value: 'nenhum' },
      { label: 'ClearSale', value: 'clearsale' },
    ],
    []
  );

  const [selectedAntifraud, setSelectedAntifraud] = useState(
    antifraudOptions[0]
  );

  const [viddyAntifraudEnabled, setViddyAntifraudEnabled] = useState(false);

  const pagarmeGatewayOption = useMemo(
    () =>
      gatewayOptions.find(option => option.value === 'pagarme') || {
        value: 'pagarme',
        label: 'Pagar.me v5',
      },
    []
  );

  const hasPagarmeCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_pagarme_key),
    [initialStore]
  );

  const hasGerencianetCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_pix_key),
    [initialStore]
  );

  const hasCieloPaymentCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_cielo_payment_credentials),
    [initialStore]
  );

  const hasRedeCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_rede_key),
    [initialStore]
  );

  const hasPagSeguroCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_pagseguro_key),
    [initialStore]
  );

  const hasMercadoPagoCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_mercadopago_key),
    [initialStore]
  );

  const hasClearSaleCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_clearsale_key),
    [initialStore]
  );

  const hasCrmBonusCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_crmbonus_key),
    [initialStore]
  );

  const hasKoinCredentialsConfigured = useMemo(
    () => Boolean(initialStore?.has_koin_key),
    [initialStore]
  );

  const enableCredentialEditing = useCallback(key => {
    setEditingCredentials(state => ({
      ...state,
      [key]: true,
    }));
  }, []);

  const renderConfiguredCredentialsNotice = useCallback(
    ({
      title = 'Credenciais configuradas',
      description = 'As credenciais atuais já estão armazenadas. Clique abaixo para substituir os valores.',
      onEdit,
      buttonText = 'Alterar credenciais',
    }) => (
      <div className="border rounded p-3 text-muted">
        <div className="font-weight-700 text-gray-700 mb-1">{title}</div>
        <div className="font-size-12 mb-3">{description}</div>
        <Button
          type="button"
          color="primary"
          className="new-layout-btn new-layout-btn-transparent"
          onClick={onEdit}
        >
          {buttonText}
        </Button>
      </div>
    ),
    []
  );

  const fillConfiguredCredentialPlaceholders = useCallback(
    (baseData, shouldPreserve, fields) => {
      if (!shouldPreserve) return baseData;

      const next = { ...baseData };

      fields.forEach(field => {
        if (!next[field]) {
          next[field] = '__configured__';
        }
      });

      return next;
    },
    []
  );

  // Qualquer edição manual do endereço desalinha o pin, tenha ele vindo
  // do geocoding ou do arrasto do operador.
  const markAddressDirty = useCallback(() => {
    if (internalAddressUpdateRef.current) return;

    setAddressSyncState('dirty');
  }, []);

  const toggleSection = useCallback(sectionKey => {
    setOpenSections(state => ({
      ...state,
      [sectionKey]: !state[sectionKey],
    }));
  }, []);

  const rememberManualPagarmeKeys = useCallback(() => {
    setManualPagarmeKeys({
      publicKey: formRef.current?.getFieldValue('pagarme_api_public_key') || '',
      secretKey: formRef.current?.getFieldValue('pagarme_api_secret_key') || '',
    });
  }, []);

  const restoreManualPagarmeKeys = useCallback(() => {
    formRef.current?.setFieldValue(
      'pagarme_api_public_key',
      manualPagarmeKeys.publicKey || ''
    );
    formRef.current?.setFieldValue(
      'pagarme_api_secret_key',
      manualPagarmeKeys.secretKey || ''
    );
  }, [manualPagarmeKeys]);

  const handleToggleViddyPay = useCallback(
    enabled => {
      if (enabled) {
        rememberManualPagarmeKeys();
        setExternalGateway(false);
        setSelectedGateway(pagarmeGatewayOption);
        setThreeDS(true);
        setSplitPayment(true);
      } else {
        restoreManualPagarmeKeys();
      }

      setViddyPayEnabled(enabled);
    },
    [pagarmeGatewayOption, rememberManualPagarmeKeys, restoreManualPagarmeKeys]
  );

  useEffect(() => {
    const getCities = async () => {
      setCityList(await getCitiesByUf(selectedState));
    };

    getCities();
  }, [selectedState]);

  useEffect(() => {
    setSelectedAntifraud(antifraudOptions[0]);
  }, [antifraudOptions]);

  const isConstitutionDateValid = value => {
    const parsedDate = isDate(value)
      ? value
      : parse(value, 'dd/MM/yyyy', new Date());

    if (isAfter(parsedDate, new Date())) {
      formRef.current.setFieldError(
        'justa_constitution_date',
        'Data de criação não pode ser no futuro'
      );
      return false;
    }

    return true;
  };

  const isOwnerBirthDateValid = value => {
    const parsedDate = isDate(value)
      ? value
      : parse(value, 'dd/MM/yyyy', new Date());

    if (isAfter(parsedDate, new Date())) {
      formRef.current.setFieldError(
        'justa_owner_birth_date',
        'Data de nascimento não pode ser no futuro'
      );
      return false;
    }

    return true;
  };

  useEffect(() => {
    document.title = 'Editar Loja | Viddy';

    const loadStore = async () => {
      try {
        setLoading(true);
        const {
          data: { store },
        } = await api.get(`stores/${store_id}`);

        setInitialStore(store);
        setExtraPersistenceFields({
          cnpj: store.cnpj || '',
          cep: store.address?.cep || '',
        });

        const initialLocation =
          store?.address?.lat && store?.address?.long
            ? {
                lat: Number(store.address.lat),
                lng: Number(store.address.long),
              }
            : null;

        setStoreLocation(initialLocation);
        setPinMovedManually(Boolean(initialLocation));
        setAddressSyncState('synced');
        setDidBootstrapMap(Boolean(initialLocation));

        setTelephone(store.telephone);
        setFinancierPhone(store.financier_telephone);
        setExternalGateway(
          Boolean(
            store.has_mercadopago_key ||
              store.has_rede_key ||
              store.has_pagseguro_key ||
              store.has_clearsale_key
          )
        );
        setCieloMerchantId(store.cielo_merchant_id);
        setCieloMerchantKey(store.cielo_merchant_key);
        setOnlinePaymentLimit(
          Number(store.online_payments_value_limit) || 1000
        );
        setColor(store?.customize?.theme_color || '#2544b1');
        setLogo(store?.customize?.brand_logo?.url);

        if (store.has_clearsale_key) {
          setSelectedAntifraud({ label: 'Clear Sale', value: 'clearsale' });
        }

        if (store?.address?.number === 'sn') {
          setNoNumber(true);
          setAddressNumber('sn');
        } else {
          setNoNumber(false);
          setAddressNumber(store?.address?.number || '');
        }

        const state = stateList.find(s => s.value === store.address?.state);
        setSelectedState(state);

        const city = store.address?.city
          ? {
              label: store.address.city,
              value: store.address.city,
            }
          : undefined;
        setSelectedCity(city);

        setMinimumDeliveryBalance(Number(store.minimum_delivery_balance) || 50);
        setMaximumDeliveryKilometer(
          Number(store.maximum_delivery_kilometer) || 30
        );
        setDefaultFreight(Number(store.default_freight) || 0);
        setMinimumValueForFreeDelivery(
          Number(store.minimum_value_for_free_delivery) || 0
        );
        setInstallmentLimitAmount(Number(store.installment_limit_amount) || 0);
        setMottuUnitCode(Number(store.mottu_unit_code) || 0);
        setMaximumInstallments(Number(store.maximum_installments) || 12);

        if (store.payment_gateway === 'cielo') {
          setSelectedGateway({ value: 'cielo', label: 'Cielo' });
        } else if (store.payment_gateway === 'pagarme' && store.use_malga) {
          setSelectedGateway({ value: 'pagarme', label: 'Pagar.me v4' });
        } else if (store.payment_gateway === 'pagarme' && !store.use_malga) {
          setSelectedGateway({ value: 'pagarme', label: 'Pagar.me v5' });
        } else if (store.payment_gateway === 'pagarme_hub') {
          setSelectedGateway({ value: 'pagarme_hub', label: 'Pagar.me Hub' });
        } else if (store.payment_gateway === 'mercadopago') {
          setSelectedGateway({ label: 'Mercado Pago', value: 'mercadopago' });
        } else if (store.payment_gateway === 'rede') {
          setSelectedGateway({ label: 'Rede', value: 'rede' });
        } else if (store.payment_gateway === 'pagseguro') {
          setSelectedGateway({ label: 'PagSeguro', value: 'pagseguro' });
        } else if (store.payment_gateway === 'justa') {
          setSelectedGateway({ label: 'Justa', value: 'justa' });
        } else if (store.payment_gateway === '') {
          setSelectedGateway({ label: 'Nenhum', value: 'nenhum' });
        }

        if (store.bnpl_payment_gateway === 'koin') {
          setSelectedBnplGateway({ label: 'Koin', value: 'koin' });
        }

        setJustaAliasName('filial');
        setJustaCnae(store?.justa_establishment?.cnae);
        setJustaConstitutionDate(store?.justa_establishment?.constitution_date);
        setJustaOwnerName(store?.justa_establishment?.owner?.name);
        setJustaOwnerCpf(store?.justa_establishment?.owner?.legal_document);
        setJustaOwnerEmail(store?.justa_establishment?.owner?.email);
        setJustaOwnerCellphone(store?.justa_establishment?.owner?.cellphone);
        setJustaOwnerPhone(store?.justa_establishment?.owner?.cellphone);
        setJustaOwnerBirthDate(store?.justa_establishment?.owner?.birth_date);
        setJustaOwnerRg(store?.justa_establishment?.owner?.rg);
        setJustaAccountCpf(
          store?.justa_establishment?.account_bank?.legal_document
        );
        setJustaAccountCode(
          store?.justa_establishment?.account_bank?.bank_code
        );
        setSelectedAccountType(store?.justa_establishment?.account_bank?.type);
        setJustaAccountAgency(store?.justa_establishment?.account_bank?.agency);
        setJustaAccountOwnerName(
          store?.justa_establishment?.account_bank?.owner_name
        );
        setJustaAccountNumber(store?.justa_establishment?.account_bank?.number);

        if (store?.justa_establishment?.account_bank?.type === 'checking') {
          setSelectedAccountType({ label: 'Corrente', value: 'checking' });
        }

        if (store?.justa_establishment?.account_bank?.type === 'saving') {
          setSelectedAccountType({ label: 'Poupança', value: 'saving' });
        }

        setExternalGateway(store.use_malga || false);
        setClearsaleAppName(store.clearsale_app);
        setSplitPayment(store.use_split);
        setHidePaymentData(store.hide_payment_data);
        setUseAutomaticFreight(store.use_automatic_freight);
        setWhatsappFlowEnabled(store.whatsapp_flow_enabled);
        setViddyAntifraudEnabled(store.antifraud_viddy_enabled);
        setThreeDS(store.use_threeds);
        setGooglePayEnabled(!!store.google_pay_enabled);
        setApplePayEnabled(!!store.apple_pay_enabled);
        setPagarmeHub(store.pagarme_hub_callback);

        setDeliveryRouterEnabled(!!store.delivery_router_enabled);
        setDeliveryRouterIncentiveStep(
          Number(store.delivery_router_incentive_step) || 0
        );

        const usesViddyPayPreset = store.payment_gateway_preset === 'viddy_pay';

        setViddyPayEnabled(usesViddyPayPreset);
        setManualPagarmeKeys({
          publicKey: usesViddyPayPreset
            ? ''
            : store.pagarme_api_public_key || '',
          secretKey: usesViddyPayPreset
            ? ''
            : store.pagarme_api_secret_key || '',
        });

        if (store.gerencianet_certificate) {
          setCertificate(store.gerencianet_certificate);
          setCertificateUrl(store.gerencianet_certificate.url);
        }

        setLoading(false);
      } catch (err) {
        history.goBack();
        console.error(err);
        if (err.response && err?.response?.data?.message) {
          toast.error(err?.response?.data?.message);
        } else {
          toast.error('Loja não encontrada.');
        }
      }
    };

    loadStore();
  }, [antifraudOptions, store_id]);

  useEffect(() => {
    if (loading || didBootstrapMap || !initialStore || !formRef.current) return;

    const hasLocation = storeLocation?.lat && storeLocation?.lng;
    const hasAddress =
      initialStore?.address?.cep ||
      initialStore?.address?.street_name ||
      initialStore?.address?.city;

    if (hasLocation) {
      setDidBootstrapMap(true);
      return;
    }

    if (hasAddress) {
      setDidBootstrapMap(true);
      setAddressGeocodeTrigger(value => value + 1);
    }
  }, [didBootstrapMap, initialStore, loading, storeLocation]);

  const adjustMask = v => {
    const digits = !v ? '' : v.replace(/[^\d]/g, '');
    if (!digits || digits.length < 10) return v;
    const cut = digits.length === 10 ? 6 : 7;
    const max = digits.length > 11 ? 11 : digits.length;
    return `(${digits.substring(0, 2)}) ${digits.substring(
      2,
      cut
    )}-${digits.substring(cut, max)}`;
  };

  const maskBuilder = v => {
    if (!v || v.length === 0) return '';
    const mask = adjustMask(v);
    return mask.length >= 6 && mask[5] === '9'
      ? '(99) 99999-9999'
      : '(99) 9999-9999';
  };

  useEffect(() => {
    const loadDeliveryOperators = async () => {
      setLoadingOperators(true);

      try {
        const { data } = await api.get(
          `administrator/stores/${store_id}/delivery-operators`
        );

        setVehicleLabels(data.available_vehicles || {});
        setDeliveryOperators(data.operators || []);
      } catch (err) {
        toast.error('Não foi possível carregar os parceiros de entrega.');
      } finally {
        setLoadingOperators(false);
      }
    };

    if (store_id) loadDeliveryOperators();
  }, [store_id]);

  const handleValidSubmit = async data => {
    try {
      formRef.current.setErrors({});
      const brandLogo = file;

      if (!storeLocation?.lat || !storeLocation?.lng) {
        toast.error('Confirme a localização da loja no mapa.');
        return;
      }

      if (addressSyncState === 'dirty') {
        toast.error(
          'Você alterou o endereço após ajustar o pin. Clique em "Recentralizar pelo endereço" ou mova o pin novamente.'
        );
        return;
      }

      const resolvedGateway =
        viddyPayEnabled && selectedGateway.value === 'pagarme'
          ? 'pagarme'
          : selectedGateway.value;

      const resolvedPreset =
        viddyPayEnabled && selectedGateway.value === 'pagarme'
          ? 'viddy_pay'
          : null;

      let parsedData = {
        ...data,
        online_payments_value_limit: onlinePaymentLimit,
        minimum_delivery_balance: minimumDeliveryBalance,
        default_freight: defaultFreight,
        minimum_value_for_free_delivery: minimumValueForFreeDelivery,
        installment_limit_amount: installmentLimitAmount,
        address: {
          ...data.address,
          number: noNumber ? 'sn' : addressNumber || data.address?.number,
          city: selectedCity?.value,
          state: selectedState?.value,
          lat: storeLocation?.lat ?? null,
          long: storeLocation?.lng ?? null,
        },
        customize: {
          theme_color: color,
        },
      };

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'pagarme' &&
          !externalGateway &&
          !viddyPayEnabled &&
          hasPagarmeCredentialsConfigured &&
          !editingCredentials.pagarme,
        ['pagarme_api_public_key', 'pagarme_api_secret_key']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'pagarme' &&
          externalGateway &&
          hasPagarmeCredentialsConfigured &&
          !editingCredentials.pagarmeExternal,
        ['pagarme_api_key']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'cielo' &&
          hasGerencianetCredentialsConfigured &&
          !editingCredentials.cieloGerencianet,
        ['gerencianet_client_id', 'gerencianet_client_secret', 'pix_key']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'cielo' &&
          hasCieloPaymentCredentialsConfigured &&
          !editingCredentials.cieloMerchant,
        ['cielo_merchant_id', 'cielo_merchant_key', 'cielo_provider_id']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'rede' &&
          hasGerencianetCredentialsConfigured &&
          !editingCredentials.redeGerencianet,
        ['gerencianet_client_id', 'gerencianet_client_secret', 'pix_key']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'rede' &&
          hasRedeCredentialsConfigured &&
          !editingCredentials.rede,
        ['rede_merchant_id', 'rede_api_key']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'pagseguro' &&
          hasPagSeguroCredentialsConfigured &&
          !editingCredentials.pagseguro,
        ['pagseguro_token', 'pagseguro_email']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedGateway.value === 'mercadopago' &&
          hasMercadoPagoCredentialsConfigured &&
          !editingCredentials.mercadopago,
        ['mercadopago_access_token']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedAntifraud.value === 'clearsale' &&
          hasClearSaleCredentialsConfigured &&
          !editingCredentials.clearsale,
        ['clearsale_name', 'clearsale_password']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        hasCrmBonusCredentialsConfigured && !editingCredentials.crmbonus,
        ['crmbonus_company_code', 'crmbonus_store_id']
      );

      parsedData = fillConfiguredCredentialPlaceholders(
        parsedData,
        selectedBnplGateway.value === 'koin' &&
          hasKoinCredentialsConfigured &&
          !editingCredentials.koin,
        ['koin_private_key']
      );

      const maskTelephoneLength = maskBuilder(telephoneV).length;
      const maskFinancierTelephoneLength = maskBuilder(financierPhone).length;
      const maskJustaOwnerCellphoneLength =
        maskBuilder(justaOwnerCellphone).length;

      await SchemaValidator(
        maskTelephoneLength,
        maskFinancierTelephoneLength,
        maskJustaOwnerCellphoneLength
      ).validate(parsedData, {
        abortEarly: false,
        context: {
          payment_gateway: resolvedGateway,
          payment_gateway_preset: resolvedPreset,
        },
      });

      const store = {
        ...parsedData,
        use_malga: externalGateway,
        payment_gateway: resolvedGateway,
        payment_gateway_preset: resolvedPreset,
        bnpl_payment_gateway: selectedBnplGateway.value,
        clearsale_app: clearsaleAppName,
        use_split: splitPayment,
        use_automatic_freight: useAutomaticFreight,
        whatsapp_flow_enabled: whatsappFlowEnabled,
        antifraud_viddy_enabled: viddyAntifraudEnabled,
        delivery_router_enabled: deliveryRouterEnabled,
        delivery_router_incentive_step: deliveryRouterIncentiveStep || 0,
        hide_payment_data: hidePaymentData,
        use_threeds: threeDS,
        google_pay_enabled: googlePayEnabled,
        apple_pay_enabled: applePayEnabled,
        pagarme_hub_key: pagarmeHub?.access_token,
        pagarme_hub_callback: pagarmeHub,
      };

      if (selectedGateway.value !== 'pagarme_hub') {
        delete store.pagarme_hub_key;
        delete store.pagarme_hub_callback;
      }

      if (selectedGateway.value === 'pagarme_hub' && !pagarmeHub) {
        store.pagarme_hub_key = null;
        store.pagarme_hub_callback = null;
      }

      if (
        resolvedPreset === 'viddy_pay' ||
        (selectedGateway.value === 'pagarme' &&
          !externalGateway &&
          hasPagarmeCredentialsConfigured &&
          !editingCredentials.pagarme)
      ) {
        delete store.pagarme_api_public_key;
        delete store.pagarme_api_secret_key;
      }

      if (
        selectedGateway.value === 'pagarme' &&
        externalGateway &&
        hasPagarmeCredentialsConfigured &&
        !editingCredentials.pagarmeExternal
      ) {
        delete store.pagarme_api_key;
      }

      if (
        selectedGateway.value === 'cielo' &&
        hasGerencianetCredentialsConfigured &&
        !editingCredentials.cieloGerencianet
      ) {
        delete store.gerencianet_client_id;
        delete store.gerencianet_client_secret;
        delete store.pix_key;
      }

      if (
        selectedGateway.value === 'cielo' &&
        hasCieloPaymentCredentialsConfigured &&
        !editingCredentials.cieloMerchant
      ) {
        delete store.cielo_merchant_id;
        delete store.cielo_merchant_key;
        delete store.cielo_provider_id;
      }

      if (
        selectedGateway.value === 'rede' &&
        hasGerencianetCredentialsConfigured &&
        !editingCredentials.redeGerencianet
      ) {
        delete store.gerencianet_client_id;
        delete store.gerencianet_client_secret;
        delete store.pix_key;
      }

      if (
        selectedGateway.value === 'rede' &&
        hasRedeCredentialsConfigured &&
        !editingCredentials.rede
      ) {
        delete store.rede_merchant_id;
        delete store.rede_api_key;
      }

      if (
        selectedGateway.value === 'pagseguro' &&
        hasPagSeguroCredentialsConfigured &&
        !editingCredentials.pagseguro
      ) {
        delete store.pagseguro_token;
        delete store.pagseguro_email;
      }

      if (
        selectedGateway.value === 'mercadopago' &&
        hasMercadoPagoCredentialsConfigured &&
        !editingCredentials.mercadopago
      ) {
        delete store.mercadopago_access_token;
        delete store.pagseguro_email;
      }

      if (
        selectedAntifraud.value === 'clearsale' &&
        hasClearSaleCredentialsConfigured &&
        !editingCredentials.clearsale
      ) {
        delete store.clearsale_name;
        delete store.clearsale_password;
      }

      if (hasCrmBonusCredentialsConfigured && !editingCredentials.crmbonus) {
        delete store.crmbonus_company_code;
        delete store.crmbonus_store_id;
      }

      if (
        selectedBnplGateway.value === 'koin' &&
        hasKoinCredentialsConfigured &&
        !editingCredentials.koin
      ) {
        delete store.koin_private_key;
      }

      if (selectedGateway.value === 'justa') {
        store.justa_establishment = {
          alias_name: justaAliasName,
          cnae: justaCnae,
          constitution_date: justaConstitutionDate,
          owner: {
            name: justaOwnerName,
            legal_document: justaOwnerCpf,
            email: justaOwnerEmail,
            cellphone: justaOwnerCellphone,
            phone: justaOwnerPhone,
            birth_date: justaOwnerBirthDate,
            rg: justaOwnerRg || '',
          },
          account_bank: {
            legal_document: justaAccountCpf,
            bank_code: justaAccountCode,
            type: selectedAccountType?.value,
            agency: justaAccountAgency,
            owner_name: justaAccountOwnerName,
            number: justaAccountNumber,
          },
        };
      }

      if (selectedAntifraud.value === 'nenhum') {
        store.remove_antifraud_provider = true;
        store.clearsale_name = null;
        store.clearsale_password = null;
        store.clearsale_app = null;
      }

      if (certificate) {
        try {
          if (certificateUrl && certificate.url) {
            await api.post(
              `administrator/stores/${store_id}/gerencianet/set-certificate`,
              { certificate: certificate.url }
            );
          } else {
            const formData = new FormData();
            formData.append('certificate', certificate);
            await api.post(
              `administrator/stores/${store_id}/gerencianet/set-certificate`,
              formData
            );
          }
        } catch (err) {
          if (err.response && err?.response?.data?.message) {
            toast.error(err?.response?.data?.message);
          }
        }
      }

      // "nenhum" e "" são estados da UI. Aqui o envio é JSON, então mandamos
      // null explícito: a API aceita (nullable) e a coluna é LIMPA.
      if (!store.payment_gateway || store.payment_gateway === 'nenhum') {
        store.payment_gateway = null;
      }

      if (
        !store.bnpl_payment_gateway ||
        store.bnpl_payment_gateway === 'nenhum'
      ) {
        store.bnpl_payment_gateway = null;
      }

      if (
        !store.payment_gateway_preset ||
        store.payment_gateway_preset === 'nenhum'
      ) {
        store.payment_gateway_preset = null;
      }

      await api.put(`administrator/stores/${store_id}`, store);

      await api.put(`administrator/stores/${store_id}/delivery-operators`, {
        operators: deliveryOperators.map(item => ({
          operator: item.operator,
          enabled: item.enabled,
          vehicles: item.vehicles,
        })),
      });

      if (removeLogo) {
        await api.post(`administrator/stores/${store_id}/brand-logo`);
      }

      if (brandLogo) {
        try {
          const formData = new FormData();
          formData.append('brand_logo', brandLogo);

          await api.post(
            `administrator/stores/${store_id}/brand-logo`,
            formData
          );
        } catch (err) {
          if (err.response && err?.response?.data?.message) {
            toast.error(err?.response?.data?.message);
          }
        }
      }

      toast.success('Loja atualizada com sucesso!');

      history.push(`/lojas`);
    } catch (err) {
      const validationErrors = {};
      if (err instanceof ValidationError) {
        err.inner.forEach(e => {
          validationErrors[e.path] = e.message;
        });
        formRef.current.setErrors(validationErrors);
      }

      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      }

      if (err.response && err.response.data.cnpj) {
        formRef.current.setFieldError('cnpj', err.response.data.cnpj[0]);
      }

      if (err.response && err.response.data.certificate) {
        toast.error(err.response.data.certificate[0]);
      }

      // Validação do Lumen: 422 com { campo: [mensagens] } e sem "message".
      // Sem este tratamento o salvamento falha silenciosamente.
      if (
        err.response &&
        err.response.status === 422 &&
        err.response.data &&
        !err.response.data.message
      ) {
        const fields = Object.keys(err.response.data);

        fields.forEach(field => {
          const messages = err.response.data[field];
          const text = Array.isArray(messages) ? messages[0] : messages;

          if (!text) return;

          try {
            formRef.current.setFieldError(field, text);
          } catch (e) {
            // Campo não existe no formulário: mostra só o toast.
          }
        });

        const firstField = fields[0];
        const firstMessage = Array.isArray(err.response.data[firstField])
          ? err.response.data[firstField][0]
          : err.response.data[firstField];

        toast.error(firstMessage || 'Verifique os campos do formulário.', {
          autoClose: 8000,
        });
      }
    }
  };

  useEffect(() => {
    if (noNumber) {
      formRef?.current?.setFieldValue('address.number', 'sn');
      setAddressNumber('sn');
      return;
    }

    if (addressNumber === 'sn') {
      formRef?.current?.setFieldValue('address.number', '');
      setAddressNumber('');
    }
  }, [addressNumber, noNumber]);

  const pagarmePublicKey = process.env.REACT_APP_PAGARME_HUB_PUBLIC_KEY;

  const getPagarmeAuthToken = useCallback(
    async authorizationCode => {
      const { data } = await axios.post(
        process.env.REACT_APP_PAGARME_HUB_API_URL,
        {
          code: authorizationCode,
          hub_callback_url: `https://${localStorage.getItem(
            '@VIDDY:X-Server'
          )}.viddy.com.br/api/v1/webhooks/pagarme/hub-callback`,
        },
        {
          headers: {
            PublicAppKey: pagarmePublicKey,
          },
        }
      );

      setPagarmeHub(data);

      return data;
    },
    [pagarmePublicKey]
  );

  const location = useLocation();

  useEffect(() => {
    const searchParams = new window.URLSearchParams(location.search);

    const storeFormState = JSON.parse(sessionStorage.getItem('storeFormState'));
    const authorizationCode = searchParams.get('authorization_code');

    if (!storeFormState || !formRef.current || !authorizationCode || loading)
      return;

    getPagarmeAuthToken(authorizationCode);

    formRef.current.setData({
      ...formRef.current.getData(),
      ...storeFormState,
    });

    setInitialStore({
      ...formRef.current.getData(),
      ...storeFormState,
    });

    if (storeFormState.city) setSelectedCity(storeFormState.city);
    if (storeFormState.state) setSelectedState(storeFormState.state);
    if (storeFormState.noNumber) setNoNumber(storeFormState.noNumber);

    if (typeof storeFormState.addressNumber !== 'undefined') {
      setAddressNumber(storeFormState.addressNumber);
    }

    if (storeFormState.storeLocation) {
      setStoreLocation(storeFormState.storeLocation);
      setPinMovedManually(
        typeof storeFormState.pinMovedManually === 'boolean'
          ? storeFormState.pinMovedManually
          : true
      );
      setDidBootstrapMap(true);
    }

    if (storeFormState.addressSyncState) {
      setAddressSyncState(storeFormState.addressSyncState);
    }

    if (typeof storeFormState.viddyPayEnabled === 'boolean') {
      setViddyPayEnabled(storeFormState.viddyPayEnabled);
    }

    if (storeFormState.manualPagarmeKeys) {
      setManualPagarmeKeys(storeFormState.manualPagarmeKeys);
    }

    if (storeFormState.editingCredentials) {
      setEditingCredentials(storeFormState.editingCredentials);
    }

    if (storeFormState.brand_logo) setFile(storeFormState.brand_logo);
    if (storeFormState.theme_color) setColor(storeFormState.theme_color);
    if (storeFormState.use_malga) setExternalGateway(storeFormState.use_malga);
    if (storeFormState.payment_gateway)
      setSelectedGateway(storeFormState.payment_gateway);
    if (storeFormState.clearsale_app)
      setClearsaleAppName(storeFormState.clearsale_app);
    if (storeFormState.use_split) setSplitPayment(storeFormState.use_split);
    if (storeFormState.hide_payment_data)
      setHidePaymentData(storeFormState.hide_payment_data);
    if (storeFormState.use_automatic_freight)
      setUseAutomaticFreight(storeFormState.use_automatic_freight);
    if (storeFormState.whatsapp_flow_enabled)
      setWhatsappFlowEnabled(storeFormState.whatsapp_flow_enabled);
    if (storeFormState.antifraud_viddy_enabled)
      setViddyAntifraudEnabled(storeFormState.antifraud_viddy_enabled);
    if (storeFormState.use_threeds) setThreeDS(storeFormState.use_threeds);
    if (storeFormState.google_pay_enabled)
      setGooglePayEnabled(storeFormState.google_pay_enabled);
    if (storeFormState.apple_pay_enabled)
      setApplePayEnabled(storeFormState.apple_pay_enabled);
    if (storeFormState.antifraud)
      setSelectedAntifraud(storeFormState.antifraud);
    if (storeFormState.financierPhone)
      setFinancierPhone(storeFormState.financierPhone);
    if (storeFormState.minimumDeliveryBalance)
      setMinimumDeliveryBalance(storeFormState.minimumDeliveryBalance);
    if (storeFormState.onlinePaymentLimit)
      setOnlinePaymentLimit(storeFormState.onlinePaymentLimit);
    if (storeFormState.maximumDeliveryKilometer)
      setMaximumDeliveryKilometer(storeFormState.maximumDeliveryKilometer);
    if (storeFormState.defaultFreight)
      setDefaultFreight(storeFormState.defaultFreight);
    if (storeFormState.minimumValueForFreeDelivery)
      setMinimumValueForFreeDelivery(
        storeFormState.minimumValueForFreeDelivery
      );
    if (storeFormState.installmentLimitAmount)
      setInstallmentLimitAmount(storeFormState.installmentLimitAmount);
    if (storeFormState.mottuUnitCode)
      setMottuUnitCode(storeFormState.mottuUnitCode);
    if (storeFormState.maximumInstallments)
      setMaximumInstallments(storeFormState.maximumInstallments);
    if (storeFormState.extraPersistenceFields)
      setExtraPersistenceFields(storeFormState.extraPersistenceFields);

    if (storeFormState.justa_cnae) setJustaCnae(storeFormState.justa_cnae);
    if (storeFormState.justa_constitution_date)
      setJustaConstitutionDate(storeFormState.justa_constitution_date);
    if (storeFormState.justa_owner_name)
      setJustaOwnerName(storeFormState.justa_owner_name);
    if (storeFormState.justa_owner_cpf)
      setJustaOwnerCpf(storeFormState.justa_owner_cpf);
    if (storeFormState.justa_owner_email)
      setJustaOwnerEmail(storeFormState.justa_owner_email);
    if (storeFormState.justa_owner_cellphone)
      setJustaOwnerCellphone(storeFormState.justa_owner_cellphone);
    if (storeFormState.justa_owner_cellphone)
      setJustaOwnerPhone(storeFormState.justa_owner_cellphone);
    if (storeFormState.justa_owner_birth_date)
      setJustaOwnerBirthDate(storeFormState.justa_owner_birth_date);
    if (storeFormState?.justa_owner_rg)
      setJustaOwnerRg(storeFormState.justa_owner_rg);
    if (storeFormState.justa_owner_cpf)
      setJustaAccountCpf(storeFormState.justa_owner_cpf);
    if (storeFormState.justa_account_bank_code)
      setJustaAccountCode(storeFormState.justa_account_bank_code);
    if (storeFormState.justa_account_bank_type)
      setSelectedAccountType(storeFormState.justa_account_bank_type);
    if (storeFormState.justa_account_bank_agency)
      setJustaAccountAgency(storeFormState.justa_account_bank_agency);
    if (storeFormState.justa_account_bank_owner_name)
      setJustaAccountOwnerName(storeFormState.justa_account_bank_owner_name);
    if (storeFormState.justa_account_bank_number)
      setJustaAccountNumber(storeFormState.justa_account_bank_number);

    if (!storeFormState.storeLocation) {
      setDidBootstrapMap(false);
      setAddressGeocodeTrigger(value => value + 1);
    }

    setOpenSections(prev => ({ ...prev, payments: true }));

    setTimeout(() => {
      const paymentsSection = document.getElementById('payments-section');
      if (paymentsSection) {
        paymentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);

    sessionStorage.clear();
  }, [getPagarmeAuthToken, location.search, loading]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const visibilitychangeHandler = async () => {
      if (document.hidden) return;

      const {
        data: { store },
      } = await api.get(`stores/${store_id}`);

      // Só atualiza se não tiver dados reais do Hub em memória
      if (!pagarmeHub?.access_token) {
        setPagarmeHub(store.pagarme_hub_callback);
      }
    };

    document.addEventListener('visibilitychange', visibilitychangeHandler);

    return () =>
      document.removeEventListener('visibilitychange', visibilitychangeHandler);
  }, [store_id, pagarmeHub]);

  const pagarmeUrl = pagarmeHub?.install_id
    ? {
        href: `${process.env.REACT_APP_PAGARME_HUB_URL}/${pagarmePublicKey}/edit/${pagarmeHub?.install_id}`,
        target: '_blank',
        rel: 'noreferrer',
      }
    : {
        href: `${process.env.REACT_APP_PAGARME_HUB_URL}/${pagarmePublicKey}/authorize?redirect=${window.location.href}`,
      };

  if (loading) {
    return (
      <div className="page-content">
        <Container className="container new-layout">
          <Loading />
        </Container>
      </div>
    );
  }

  const showConfiguredPagarmeNotice =
    hasPagarmeCredentialsConfigured && !editingCredentials.pagarme;
  const showClearSaleSection = selectedAntifraud?.value === 'clearsale';

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="EDITAR LOJA"
          backTo="/lojas"
          responsivePosition="start"
          desktopPosition="center"
        />

        <Form
          onSubmit={handleValidSubmit}
          ref={formRef}
          initialData={
            initialStore && {
              ...initialStore,
              bank_data: {
                ...initialStore.bank_data,
                institution: {
                  value: initialStore.bank_data?.institution_code || '',
                  label: initialStore.bank_data?.institution || 'Instituição',
                },
                document_type: {
                  value: initialStore.bank_data?.document_type || '',
                  label:
                    initialStore.bank_data?.document_type ||
                    'Selecione o tipo do documento',
                },
              },
            }
          }
          noValidate
        >
          <SectionCard
            title="Dados da Loja"
            subtitle="Informações principais usadas no cadastro da loja."
          >
            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>Razão Social</Label>
                  <Input name="name" placeholder="Razão Social" type="text" />
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Nome Fantasia</Label>
                  <Input
                    name="fantasy_name"
                    placeholder="Nome Fantasia"
                    type="text"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>CNPJ</Label>
                  <InputMask
                    name="cnpj"
                    placeholder="CNPJ"
                    mask="99.999.999/9999-99"
                    maskChar={null}
                    type="text"
                    value={extraPersistenceFields.cnpj ?? ''}
                    onChange={e =>
                      setExtraPersistenceFields(state => ({
                        ...state,
                        cnpj: e.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Nome do Responsável</Label>
                  <Input
                    name="responsible_name"
                    placeholder="Nome do Responsável"
                    type="text"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>E-mail</Label>
                  <Input name="email" placeholder="E-mail" type="text" />
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Telefone</Label>
                  <InputMask
                    mask={maskBuilder(telephoneV)}
                    maskChar={null}
                    defaultValue={telephoneV}
                    name="telephone"
                    placeholder="Telefone"
                    type="text"
                    onChange={e => setTelephone(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </SectionCard>

          <SectionCard
            title="Endereço e Localização"
            subtitle="Confirme o endereço e ajuste o pin para o ponto exato da loja."
          >
            <Scope path="address">
              <div className="d-none" aria-hidden="true">
                <InputMask
                  name="cep"
                  mask="99999-999"
                  maskChar={null}
                  value={extraPersistenceFields.cep ?? ''}
                  onChange={e => {
                    const cep = e.target.value;

                    setExtraPersistenceFields(state => ({
                      ...state,
                      cep,
                    }));
                  }}
                />
              </div>
              {initialStore?.address &&
                initialStore.address.has_coordinates === false && (
                  <div className="alert alert-warning py-2 px-3 mb-3">
                    Esta loja está <strong>sem coordenadas</strong>. A validação
                    de raio de entrega e as cotações que dependem de localização
                    não funcionarão até que o pin seja posicionado no mapa.
                    Ajuste o pin e salve.
                  </div>
                )}
              <StoreLocationPicker
                value={storeLocation}
                formRef={formRef}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                addressNumber={addressNumber}
                setAddressNumber={setAddressNumber}
                noNumber={noNumber}
                setNoNumber={setNoNumber}
                setExtraPersistenceFields={setExtraPersistenceFields}
                onLocationChange={setStoreLocation}
                pinMovedManually={pinMovedManually}
                setPinMovedManually={setPinMovedManually}
                addressSyncState={addressSyncState}
                setAddressSyncState={setAddressSyncState}
                addressGeocodeTrigger={addressGeocodeTrigger}
                internalAddressUpdateRef={internalAddressUpdateRef}
              />

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Logradouro</Label>
                    <Input
                      name="street_name"
                      placeholder="Logradouro"
                      type="text"
                      onBlur={markAddressDirty}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <Label>Número</Label>

                  <CustomInput
                    type="switch"
                    id="noNumberSwitch"
                    name="noNumber"
                    label="Sem Número"
                    checked={noNumber}
                    className="text-primary float-right"
                    onChange={() => {
                      setNoNumber(value => !value);
                    }}
                  />
                  <FormGroup>
                    <Input
                      name="number"
                      placeholder="Número"
                      type="tel"
                      disabled={noNumber}
                      value={noNumber ? 'sn' : addressNumber || ''}
                      onChange={({ target: { value } }) => {
                        setAddressNumber(value);
                        markAddressDirty();
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Bairro</Label>
                    <Input
                      name="neighborhood"
                      placeholder="Bairro"
                      type="text"
                      onBlur={markAddressDirty}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <FormGroup>
                    <Label>Complemento</Label>
                    <Input
                      name="complement"
                      placeholder="Complemento"
                      type="text"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Estado</Label>
                    <InputSelect
                      name="state"
                      options={stateList}
                      onChange={selectedOption => {
                        setSelectedState(selectedOption);
                        markAddressDirty();
                      }}
                      value={selectedState}
                      placeholder="Estado"
                      singlePath
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <FormGroup>
                    <Label>Cidade</Label>
                    <InputSelect
                      name="city"
                      options={cityList}
                      onChange={selectedOption => {
                        setSelectedCity(selectedOption);
                        markAddressDirty();
                      }}
                      value={selectedCity}
                      placeholder="Cidade"
                      isDisabled={!selectedState}
                      singlePath
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="w-100 mx-0 mb-4">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Informações Adicionais</Label>
                    <Input
                      name="additional_information"
                      placeholder="Informações Adicionais"
                      type="text"
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <FormGroup>
                    <Label>Telefone do Financeiro</Label>
                    <InputMask
                      mask={maskBuilder(financierPhone)}
                      maskChar={null}
                      value={financierPhone}
                      name="financier_telephone"
                      placeholder="Celular do financeiro para comunicação mais rápida"
                      type="text"
                      onChange={e => setFinancierPhone(e.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Scope>
          </SectionCard>
          <div id="payments-section">
            <SectionCard
              title="Informações de Pagamento"
              subtitle="Gateway de pagamento, Viddy Pay, Antifraude Viddy."
              collapsible
              isOpen={openSections.payments}
              onToggle={() => toggleSection('payments')}
            >
              <SubSectionBlock>
                <Row className="w-100 mx-0">
                  <Col lg={6} className="px-0 pr-lg-2">
                    <Label>Gateway de pagamento</Label>

                    <CustomInput
                      type="switch"
                      id="externalSwitch"
                      name="externalGateway"
                      label="Externo"
                      checked={externalGateway}
                      className="text-primary float-right"
                      onChange={() => {
                        setExternalGateway(state => !state);
                        setSelectedGateway(gatewayOptions[0]);
                        setViddyPayEnabled(false);
                      }}
                    />
                    <FormGroup>
                      <InputSelect
                        name="payment_gateway"
                        className="common-select"
                        options={
                          externalGateway
                            ? externalGatewayOptions
                            : gatewayOptions
                        }
                        onChange={selectedOption => {
                          setSelectedGateway(selectedOption);

                          if (selectedOption?.value !== 'pagarme') {
                            setViddyPayEnabled(false);
                          }
                        }}
                        value={selectedGateway || ''}
                        placeholder="Gateway switch"
                        singlePath
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="px-0 pl-lg-2">
                    <FormGroup>
                      <Label>Antifraude</Label>
                      <InputSelect
                        name="antifraud"
                        className="common-select"
                        options={antifraudOptions}
                        value={selectedAntifraud}
                        onChange={selectedOption => {
                          setSelectedAntifraud(selectedOption);
                        }}
                        placeholder="Selecione o antifraude"
                        singlePath
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </SubSectionBlock>

              {showClearSaleSection && (
                <SubSectionBlock
                  title="Configuração do antifraude: ClearSale"
                  hint="Credenciais utilizadas pelo antifraude ClearSale."
                >
                  {hasClearSaleCredentialsConfigured &&
                  !editingCredentials.clearsale ? (
                    <>
                      <Row className="w-100 mx-0 mb-3">
                        <Col xs={12} className="px-0">
                          {renderConfiguredCredentialsNotice({
                            title: 'Credenciais configuradas',
                            description:
                              'As credenciais da ClearSale já estão armazenadas. Clique abaixo para substituir os valores.',
                            onEdit: () => enableCredentialEditing('clearsale'),
                          })}
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <>
                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>Usuário</Label>
                            <Input
                              name="clearsale_name"
                              placeholder="Usuário"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={6} className="px-0 pl-lg-2">
                          <FormGroup>
                            <Label>Senha</Label>
                            <Input
                              name="clearsale_password"
                              placeholder="Senha"
                              type="password"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>Nome do App</Label>
                            <Input
                              onChange={e =>
                                setClearsaleAppName(e.target.value)
                              }
                              value={clearsaleAppName}
                              name="clearsale_app"
                              placeholder="Nome do App"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}
                </SubSectionBlock>
              )}

              {selectedGateway.value === 'justa' && (
                <SubSectionBlock
                  title="Configuração do gateway: Justa"
                  hint="Dados empresariais, responsável e conta bancária."
                >
                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>CNAE</Label>
                        <Input
                          name="justa_cnae"
                          value={justaCnae || ''}
                          onChange={({ target: { value } }) => {
                            setJustaCnae(value);
                          }}
                          placeholder="CNAE"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>Data de Criação</Label>
                        <InputMask
                          name="justa_constitution_date"
                          placeholder="Data de Criação"
                          mask="99/99/9999"
                          maskChar={null}
                          value={justaConstitutionDate || ''}
                          onChange={({ target: { value } }) => {
                            setJustaConstitutionDate(value);
                          }}
                          onBlur={e => isConstitutionDateValid(e.target.value)}
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>Nome do Sócio</Label>
                        <Input
                          name="justa_owner_name"
                          placeholder="Nome do Responsável"
                          value={justaOwnerName || ''}
                          onChange={({ target: { value } }) => {
                            setJustaOwnerName(value);
                          }}
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>Data de Nascimento</Label>
                        <InputMask
                          name="justa_owner_birth_date"
                          placeholder="Data de Nascimento"
                          mask="99/99/9999"
                          value={justaOwnerBirthDate || ''}
                          onChange={({ target: { value } }) => {
                            setJustaOwnerBirthDate(value);
                          }}
                          maskChar={null}
                          onBlur={e => isOwnerBirthDateValid(e.target.value)}
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>RG</Label>
                        <Input
                          name="justa_owner_rg"
                          placeholder="RG"
                          value={justaOwnerRg || ''}
                          onChange={({ target: { value } }) => {
                            setJustaOwnerRg(value);
                          }}
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>CPF</Label>
                        <InputMask
                          name="justa_owner_cpf"
                          placeholder="CPF"
                          mask="999.999.999-99"
                          value={justaOwnerCpf || ''}
                          onChange={({ target: { value } }) => {
                            setJustaOwnerCpf(value);
                          }}
                          maskChar={null}
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>E-mail</Label>
                        <Input
                          name="justa_owner_email"
                          type="text"
                          placeholder="E-mail"
                          value={justaOwnerEmail || ''}
                          onChange={({ target: { value } }) => {
                            setJustaOwnerEmail(value);
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>Celular</Label>
                        <InputMask
                          mask={maskBuilder(justaOwnerCellphone)}
                          maskChar={null}
                          name="justa_owner_cellphone"
                          placeholder="Celular"
                          type="text"
                          value={justaOwnerCellphone || ''}
                          onChange={({ target: { value } }) => {
                            if (value.length < 15 || value[5] === '9') {
                              setJustaOwnerCellphone(value);
                            }
                          }}
                          onBlur={({ target: { value } }) => {
                            setJustaOwnerCellphone(value);
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>Tipo de Conta</Label>
                        <InputSelect
                          name="justa_account_bank_type"
                          options={accountTypes}
                          value={selectedAccountType || ''}
                          onChange={selectedOption => {
                            const selected = accountTypes.find(
                              option => option.value === selectedOption.value
                            );
                            setSelectedAccountType(selected);
                          }}
                          placeholder="Selecione o Tipo de Conta"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>Código do Banco</Label>
                        <Input
                          name="justa_account_bank_code"
                          type="number"
                          placeholder="Código do Banco"
                          value={justaAccountCode || ''}
                          onChange={({ target: { value } }) => {
                            setJustaAccountCode(value);
                          }}
                          maxLength="3"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>Agência</Label>
                        <Input
                          name="justa_account_bank_agency"
                          type="string"
                          placeholder="Agência"
                          value={justaAccountAgency || ''}
                          onChange={({ target: { value } }) => {
                            setJustaAccountAgency(value);
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>Número da Conta</Label>
                        <Input
                          name="justa_account_bank_number"
                          type="string"
                          placeholder="Número da Conta"
                          value={justaAccountNumber || ''}
                          onChange={e => {
                            const rawValue = e.target.value.replace(/\D/g, '');

                            const formattedValue =
                              rawValue.length > 1
                                ? `${rawValue.slice(0, -1)}-${rawValue.slice(
                                    -1
                                  )}`
                                : rawValue;

                            e.target.value = formattedValue;
                            setJustaAccountNumber(formattedValue);
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>CPF</Label>
                        <InputMask
                          name="justa_account_bank_cpf"
                          type="text"
                          placeholder="CPF"
                          mask="999.999.999-99"
                          value={justaAccountCpf || ''}
                          onChange={({ target: { value } }) => {
                            setJustaAccountCpf(value);
                          }}
                          maskChar={null}
                          onBlur={async e => {
                            try {
                              await cpfSchema.validate(e.target.value);
                            } catch (err) {
                              formRef.current.setFieldError(
                                'justa_account_bank_cpf',
                                err.message
                              );
                            }
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>Responsável pela Conta</Label>
                        <Input
                          name="justa_account_bank_owner_name"
                          type="text"
                          placeholder="Responsável pela Conta"
                          value={justaAccountOwnerName || ''}
                          onChange={({ target: { value } }) => {
                            setJustaAccountOwnerName(value);
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </SubSectionBlock>
              )}

              {selectedGateway.value === 'cielo' && (
                <SubSectionBlock
                  title="Configuração do gateway: Cielo"
                  hint="Gerência NET, certificado e dados da Cielo."
                >
                  <h5 className="mb-2">Gerência NET</h5>
                  {hasGerencianetCredentialsConfigured &&
                  !editingCredentials.cieloGerencianet ? (
                    <Row className="w-100 mx-0 mb-3">
                      <Col xs={12} className="px-0">
                        {renderConfiguredCredentialsNotice({
                          title: 'Credenciais configuradas',
                          description:
                            'As credenciais da Gerência NET já estão armazenadas. Clique abaixo para substituir os valores.',
                          onEdit: () =>
                            enableCredentialEditing('cieloGerencianet'),
                        })}
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>Client ID</Label>
                            <Input
                              name="gerencianet_client_id"
                              placeholder="Client ID"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={6} className="px-0 pl-lg-2">
                          <FormGroup>
                            <Label>Client Secret</Label>
                            <Input
                              name="gerencianet_client_secret"
                              placeholder="Client Secret"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>Chave Pix</Label>
                            <Input
                              name="pix_key"
                              placeholder="Chave Pix"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={6} className="px-0 pl-lg-2">
                          <FormGroup className="col px-0">
                            <Label className="d-block">Certificado PEM</Label>
                            <Row
                              className={`w-100 mx-0 d-block font-weight-500 text-${
                                certificate ? 'success' : 'gray-700'
                              }`}
                            >
                              <div className="d-inline">
                                {certificate?.name ||
                                  'Nenhum arquivo selecionado'}
                              </div>
                              {certificate && (
                                <IconButton
                                  id="clean-certificate"
                                  icon="las la-times"
                                  color="danger"
                                  click={() => {
                                    certificateRef.current.value = null;
                                    setCertificate(null);
                                  }}
                                  tip="Limpar"
                                  forceTip
                                  fontSize={18}
                                  containerClass="d-inline ml-2"
                                />
                              )}
                              <a
                                onClick={() => certificateRef.current?.click()}
                                className="mt-2 d-block"
                              >
                                {certificate
                                  ? 'Escolher outro arquivo'
                                  : 'Fazer Upload'}
                              </a>
                            </Row>
                            <input
                              type="file"
                              id="certificate"
                              accept=".pem"
                              ref={certificateRef}
                              className="d-none"
                              onChange={e => {
                                const { files } = e.target;
                                try {
                                  setCertificate(files[0]);
                                } catch (err) {
                                  if (
                                    err.response &&
                                    err?.response?.data?.message
                                  ) {
                                    toast.error(err?.response?.data?.message);
                                  }
                                }
                              }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}

                  <h5 className="mb-2">Cielo</h5>
                  {hasCieloPaymentCredentialsConfigured &&
                  !editingCredentials.cieloMerchant ? (
                    <Row className="w-100 mx-0 mb-3">
                      <Col xs={12} className="px-0">
                        {renderConfiguredCredentialsNotice({
                          title: 'Credenciais configuradas',
                          description:
                            'As credenciais da Cielo já estão armazenadas. Clique abaixo para substituir Merchant ID, Merchant Key e MID Cybersource.',
                          onEdit: () =>
                            enableCredentialEditing('cieloMerchant'),
                        })}
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>Merchant ID</Label>
                            <Input
                              name="cielo_merchant_id"
                              placeholder="Merchant ID"
                              type="text"
                              defaultValue={cieloMerchantId}
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={6} className="px-0 pl-lg-2">
                          <FormGroup>
                            <Label>Merchant Key</Label>
                            <Input
                              name="cielo_merchant_key"
                              placeholder="Merchant Key"
                              type="text"
                              defaultValue={cieloMerchantKey}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>MID Cybersource (AntiFraude)</Label>
                            <Input
                              name="cielo_provider_id"
                              placeholder="Provider ID"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}
                </SubSectionBlock>
              )}

              {['pagarme', 'pagarme_hub'].includes(selectedGateway?.value) && (
                <SubSectionBlock
                  title="Configuração do gateway: Pagar.me"
                  hint="Credenciais, integração e módulos adicionais."
                >
                  {!externalGateway && (
                    <>
                      {selectedGateway?.value === 'pagarme' && (
                        <>
                          <Row className="w-100 mx-0 mb-3">
                            <Col xs={12} className="px-0">
                              <div className="border rounded px-3 py-3">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                  <div className="pr-3">
                                    <div className="font-weight-700 text-gray-700">
                                      Módulos adicionais
                                    </div>
                                    <div className="text-muted font-size-12 mt-1">
                                      Ajuste Split e 3DS independentemente do
                                      tipo de credencial utilizado.
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center mt-3 mt-lg-0">
                                    <CustomInput
                                      type="switch"
                                      id="threeDS"
                                      name="threeDS"
                                      label="3DS"
                                      checked={threeDS}
                                      className="text-primary mr-4"
                                      onChange={() => setThreeDS(!threeDS)}
                                    />
                                    <CustomInput
                                      type="switch"
                                      id="split"
                                      name="split"
                                      label="Split"
                                      checked={splitPayment}
                                      className="text-primary"
                                      onChange={() =>
                                        setSplitPayment(!splitPayment)
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </Col>
                          </Row>

                          <WalletSettingsBlock
                            googlePayEnabled={googlePayEnabled}
                            applePayEnabled={applePayEnabled}
                            onGooglePayChange={setGooglePayEnabled}
                            onApplePayChange={setApplePayEnabled}
                          />

                          {viddyPayEnabled && (
                            <Row className="w-100 mx-0">
                              <Col xs={12} className="px-0">
                                <div className="border rounded p-3 text-muted">
                                  <div className="font-weight-700 text-gray-700 mb-1">
                                    Credenciais gerenciadas pelo Viddy
                                  </div>
                                  <div className="font-size-12">
                                    O preset aplica automaticamente as
                                    credenciais. Split e 3DS continuam
                                    disponíveis para ajuste.
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          )}

                          {!viddyPayEnabled && showConfiguredPagarmeNotice && (
                            <Row className="w-100 mx-0">
                              <Col xs={12} className="px-0">
                                {renderConfiguredCredentialsNotice({
                                  title: 'Credenciais configuradas',
                                  description:
                                    'As credenciais atuais da Pagar.me já estão armazenadas. Clique abaixo para substituir os valores.',
                                  onEdit: () =>
                                    enableCredentialEditing('pagarme'),
                                })}
                              </Col>
                            </Row>
                          )}

                          {!viddyPayEnabled && !showConfiguredPagarmeNotice && (
                            <Row className="w-100 mx-0">
                              <Col lg={6} className="px-0 pr-lg-2">
                                <FormGroup>
                                  <Label>Chave Pública</Label>
                                  <Input
                                    name="pagarme_api_public_key"
                                    placeholder="Public Key"
                                    type="text"
                                  />
                                </FormGroup>
                              </Col>

                              <Col lg={6} className="px-0 pl-lg-2">
                                <FormGroup>
                                  <Label>Chave Secreta</Label>
                                  <Input
                                    name="pagarme_api_secret_key"
                                    placeholder="Private Key"
                                    type="text"
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                          )}
                        </>
                      )}
                      {selectedGateway?.value === 'pagarme_hub' && (
                        <>
                          <Row className="w-100 mx-0 mb-3">
                            <Col xs={12} className="px-0">
                              <div className="border rounded px-3 py-3">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                  <div className="pr-3">
                                    <div className="font-weight-700 text-gray-700">
                                      Módulos adicionais
                                    </div>
                                    <div className="text-muted font-size-12 mt-1">
                                      Ative ou Desative o 3DS e Split de
                                      Pagamento.
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center mt-3 mt-lg-0">
                                    <CustomInput
                                      type="switch"
                                      id="threeDSHub"
                                      name="threeDS"
                                      label="3DS"
                                      checked={threeDS}
                                      className="text-primary mr-4"
                                      onChange={() => setThreeDS(!threeDS)}
                                    />
                                    <CustomInput
                                      type="switch"
                                      id="splitHub"
                                      name="split"
                                      label="Split"
                                      checked={splitPayment}
                                      className="text-primary"
                                      onChange={() =>
                                        setSplitPayment(!splitPayment)
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </Col>
                          </Row>

                          <WalletSettingsBlock
                            idSuffix="Hub"
                            googlePayEnabled={googlePayEnabled}
                            applePayEnabled={applePayEnabled}
                            onGooglePayChange={setGooglePayEnabled}
                            onApplePayChange={setApplePayEnabled}
                            isHub
                          />
                          <Row className="w-100 mx-0">
                            <Col lg={6} className="px-0 pr-lg-2">
                              <FormGroup>
                                <Button
                                  color="primary"
                                  size="lg"
                                  type="button"
                                  className="new-layout-btn new-layout-btn-transparent main d-flex align-items-center px-0 w-100"
                                  onClick={() => {
                                    let data = formRef.current.getData();
                                    data = {
                                      ...data,
                                      ...extraPersistenceFields,
                                      city: selectedCity,
                                      state: selectedState,
                                      telephone: telephoneV,
                                      minimum_delivery_balance:
                                        data.minimum_delivery_balance,
                                      maximum_delivery_kilometer:
                                        data.maximum_delivery_kilometer,
                                      online_payments_value_limit:
                                        data.online_payments_value_limit,
                                      default_freight: data.default_freight,
                                      installment_limit_amount:
                                        data.installment_limit_amount,
                                      brand_logo: file,
                                      theme_color: color,
                                      use_malga: externalGateway,
                                      payment_gateway: selectedGateway,
                                      clearsale_app: clearsaleAppName,
                                      use_split: splitPayment,
                                      use_automatic_freight:
                                        useAutomaticFreight,
                                      whatsapp_flow_enabled:
                                        whatsappFlowEnabled,
                                      antifraud_viddy_enabled:
                                        viddyAntifraudEnabled,
                                      use_threeds: threeDS,
                                      google_pay_enabled: googlePayEnabled,
                                      apple_pay_enabled: applePayEnabled,
                                      antifraud: selectedAntifraud,
                                      onlinePaymentLimit,
                                      minimumDeliveryBalance,
                                      maximumDeliveryKilometer,
                                      defaultFreight,
                                      minimumValueForFreeDelivery,
                                      installmentLimitAmount,
                                      maximumInstallments,
                                      mottuUnitCode,
                                      financierPhone,
                                      noNumber,
                                      extraPersistenceFields,
                                      addressNumber,
                                      storeLocation,
                                      pinMovedManually,
                                      addressSyncState,
                                      viddyPayEnabled,
                                      manualPagarmeKeys,
                                      editingCredentials,
                                      openSections: {
                                        ...openSections,
                                        payments: true,
                                      },
                                    };
                                    sessionStorage.setItem(
                                      'storeFormState',
                                      JSON.stringify(data)
                                    );
                                  }}
                                >
                                  <a
                                    {...pagarmeUrl}
                                    className="text-primary w-100 h-100"
                                  >
                                    <img
                                      src="https://pagar.me/favicon-32x32.png"
                                      alt="Ícone do pagar.me"
                                      style={{ marginTop: '-2px' }}
                                      width={24}
                                      height={24}
                                    />
                                    <span className="ml-3">
                                      {pagarmeHub?.install_id
                                        ? 'Visualizar Integração'
                                        : 'Integrar com Pagarme'}
                                    </span>
                                  </a>
                                </Button>
                              </FormGroup>
                            </Col>
                            {pagarmeHub?.install_id && (
                              <Col lg={6} className="px-0 pl-lg-2">
                                <FormGroup>
                                  <Button
                                    color="danger"
                                    size="lg"
                                    type="button"
                                    className="new-layout-btn main mb-2 mr-lg-2 w-100"
                                    outline
                                    onClick={() => setShowRemoveHubModal(true)}
                                  >
                                    <i className="las la-unlink font-size-20" />
                                    <span className="ml-2">
                                      Remover Integração
                                    </span>
                                  </Button>
                                </FormGroup>
                              </Col>
                            )}
                          </Row>
                        </>
                      )}
                    </>
                  )}

                  {externalGateway && (
                    <Row className="w-100 mx-0">
                      <Col xs={12} className="px-0 pr-lg-2">
                        {hasPagarmeCredentialsConfigured &&
                        !editingCredentials.pagarmeExternal ? (
                          renderConfiguredCredentialsNotice({
                            title: 'Credenciais configuradas',
                            description:
                              'A chave externa do Pagar.me já está armazenada. Clique abaixo para substituir o valor.',
                            onEdit: () =>
                              enableCredentialEditing('pagarmeExternal'),
                          })
                        ) : (
                          <FormGroup>
                            <Label>Pagar.me Key</Label>
                            <Input
                              name="pagarme_api_key"
                              placeholder="Pagar.me key"
                              type="text"
                            />
                          </FormGroup>
                        )}
                      </Col>
                    </Row>
                  )}
                </SubSectionBlock>
              )}

              {selectedGateway.value === 'pagseguro' && (
                <SubSectionBlock
                  title="Configuração do gateway: PagSeguro"
                  hint="Token e e-mail da conta."
                >
                  {hasPagSeguroCredentialsConfigured &&
                  !editingCredentials.pagseguro ? (
                    <Row className="w-100 mx-0 mb-3">
                      <Col xs={12} className="px-0">
                        {renderConfiguredCredentialsNotice({
                          title: 'Credenciais configuradas',
                          description:
                            'As credenciais do PagSeguro já estão armazenadas. Clique abaixo para substituir Token e E-mail.',
                          onEdit: () => enableCredentialEditing('pagseguro'),
                        })}
                      </Col>
                    </Row>
                  ) : (
                    <Row className="w-100 mx-0">
                      <Col lg={6} className="px-0 pr-lg-2">
                        <FormGroup>
                          <Label>Token</Label>
                          <Input
                            name="pagseguro_token"
                            placeholder="Token"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={6} className="px-0 pl-lg-2">
                        <FormGroup>
                          <Label>E-mail</Label>
                          <Input
                            name="pagseguro_email"
                            placeholder="E-mail"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                </SubSectionBlock>
              )}

              {selectedGateway.value === 'rede' && (
                <SubSectionBlock
                  title="Configuração do gateway: Rede"
                  hint="Credenciais da Gerência NET e dados da Rede."
                >
                  <h5 className="mb-2">Gerência NET</h5>
                  {hasGerencianetCredentialsConfigured &&
                  !editingCredentials.redeGerencianet ? (
                    <Row className="w-100 mx-0 mb-3">
                      <Col xs={12} className="px-0">
                        {renderConfiguredCredentialsNotice({
                          title: 'Credenciais configuradas',
                          description:
                            'As credenciais da Gerência NET já estão armazenadas. Clique abaixo para substituir os valores.',
                          onEdit: () =>
                            enableCredentialEditing('redeGerencianet'),
                        })}
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>Client ID</Label>
                            <Input
                              name="gerencianet_client_id"
                              placeholder="Client ID"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={6} className="px-0 pl-lg-2">
                          <FormGroup>
                            <Label>Client Secret</Label>
                            <Input
                              name="gerencianet_client_secret"
                              placeholder="Client Secret"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row className="w-100 mx-0">
                        <Col lg={6} className="px-0 pr-lg-2">
                          <FormGroup>
                            <Label>Chave Pix</Label>
                            <Input
                              name="pix_key"
                              placeholder="Chave Pix"
                              type="text"
                            />
                          </FormGroup>
                        </Col>
                        <Col lg={6} className="px-0 pl-lg-2">
                          <FormGroup className="col px-0">
                            <Label className="d-block">Certificado PEM</Label>
                            <Row
                              className={`w-100 mx-0 d-block font-weight-500 text-${
                                certificate ? 'success' : 'gray-700'
                              }`}
                            >
                              <div className="d-inline">
                                {certificate?.name ||
                                  'Nenhum arquivo selecionado'}
                              </div>
                              {certificate && (
                                <IconButton
                                  id="clean-certificate-rede"
                                  icon="las la-times"
                                  color="danger"
                                  click={() => {
                                    certificateRef.current.value = null;
                                    setCertificate(null);
                                  }}
                                  tip="Limpar"
                                  forceTip
                                  fontSize={18}
                                  containerClass="d-inline ml-2"
                                />
                              )}
                              <a
                                onClick={() => certificateRef.current?.click()}
                                className="mt-2 d-block"
                              >
                                {certificate
                                  ? 'Escolher outro arquivo'
                                  : 'Fazer Upload'}
                              </a>
                            </Row>
                            <input
                              type="file"
                              id="certificate-rede"
                              accept=".pem"
                              ref={certificateRef}
                              className="d-none"
                              onChange={e => {
                                const { files } = e.target;
                                try {
                                  setCertificate(files[0]);
                                } catch (err) {
                                  if (
                                    err.response &&
                                    err?.response?.data?.message
                                  ) {
                                    toast.error(err?.response?.data?.message);
                                  }
                                }
                              }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}

                  <h5 className="mb-2">Rede</h5>
                  {hasRedeCredentialsConfigured && !editingCredentials.rede ? (
                    <Row className="w-100 mx-0 mb-3">
                      <Col xs={12} className="px-0">
                        {renderConfiguredCredentialsNotice({
                          title: 'Credenciais configuradas',
                          description:
                            'As credenciais da Rede já estão armazenadas. Clique abaixo para substituir Merchant ID e API Key.',
                          onEdit: () => enableCredentialEditing('rede'),
                        })}
                      </Col>
                    </Row>
                  ) : (
                    <Row className="w-100 mx-0">
                      <Col lg={6} className="px-0 pr-lg-2">
                        <FormGroup>
                          <Label>Merchant ID</Label>
                          <Input
                            name="rede_merchant_id"
                            placeholder="Merchant ID"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={6} className="px-0 pl-lg-2">
                        <FormGroup>
                          <Label>API Key</Label>
                          <Input
                            name="rede_api_key"
                            placeholder="API Key"
                            type="text"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                </SubSectionBlock>
              )}

              {selectedGateway.value === 'mercadopago' && (
                <SubSectionBlock
                  title="Configuração do gateway: Mercado Pago"
                  hint="Access token utilizado pela loja."
                >
                  <Row className="w-100 mx-0">
                    <Col lg={12} className="px-0 pr-lg-2">
                      {hasMercadoPagoCredentialsConfigured &&
                      !editingCredentials.mercadopago ? (
                        renderConfiguredCredentialsNotice({
                          title: 'Credencial configurada',
                          description:
                            'O access token do Mercado Pago já está armazenado. Clique abaixo para substituir o valor.',
                          onEdit: () => enableCredentialEditing('mercadopago'),
                        })
                      ) : (
                        <FormGroup>
                          <Label>Access Key</Label>
                          <Input
                            name="mercadopago_access_token"
                            placeholder="Access Key"
                            type="text"
                          />
                        </FormGroup>
                      )}
                    </Col>
                  </Row>
                </SubSectionBlock>
              )}

              {!externalGateway && (
                <div className="border rounded p-3 mb-4">
                  <Row className="mx-0 align-items-center">
                    <Col xs="auto" className="px-0 pr-3">
                      <h5 className="mb-0">Viddy Pay</h5>
                    </Col>
                    <Col xs="auto" className="px-0 ml-auto">
                      <CustomInput
                        type="switch"
                        id="viddyPaySwitch"
                        name="viddyPay"
                        label={viddyPayEnabled ? 'Ativado' : 'Desativado'}
                        checked={viddyPayEnabled}
                        className="text-primary"
                        onChange={e => handleToggleViddyPay(e.target.checked)}
                      />
                    </Col>
                  </Row>
                  <Row className="mx-0 mt-2">
                    <Col className="px-0 text-muted">
                      Ative para usar o preset operacional da Pagar.me com
                      credenciais gerenciadas pelo Viddy.
                    </Col>
                  </Row>
                </div>
              )}

              <div className="border rounded p-3">
                <Row className="mx-0 align-items-center">
                  <Col xs="auto" className="px-0 pr-3">
                    <h5 className="mb-0">AntiFraude Viddy</h5>
                  </Col>
                  <Col xs="auto" className="px-0 ml-auto">
                    <CustomInput
                      type="switch"
                      id="viddyAntifraudSwitch"
                      name="viddy_antifraud_enabled"
                      label={viddyAntifraudEnabled ? 'Ativado' : 'Desativado'}
                      checked={viddyAntifraudEnabled}
                      className="text-primary"
                      onChange={() => setViddyAntifraudEnabled(s => !s)}
                    />
                  </Col>
                </Row>
                <Row className="mx-0 mt-2">
                  <Col className="px-0 text-muted">
                    Quando ativado, toda transação (aprovada ou recusada pelo
                    antifraude) é colocada <strong>Em Análise</strong> para
                    revisão manual. Você poderá Aprovar ou Estornar. Se o
                    antifraude do gateway tiver recusado, reprocese no gateway e
                    depois Aprove no Viddy.
                  </Col>
                </Row>
              </div>
            </SectionCard>
          </div>

          <SectionCard
            title="Customização do Checkout"
            subtitle="Logomarca e cor principal da loja."
            collapsible
            isOpen={openSections.branding}
            onToggle={() => toggleSection('branding')}
          >
            <Scope path="customize">
              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Logo que será exibida no checkout.</Label>
                    <ImageInput
                      name="brand_logo"
                      logo={logo}
                      setLogo={setLogo}
                      setFile={setFile}
                      setRemoveLogo={setRemoveLogo}
                    />
                    <span className="font-weight-400 font-size-12">
                      Recomendamos que seja em png transparente
                    </span>
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Cor do header no checkout</Label>
                    <ColorPicker color={color} setColor={setColor} />
                  </FormGroup>
                </Col>
              </Row>
            </Scope>
          </SectionCard>

          <SectionCard
            title="Informações Técnicas"
            subtitle="Limites, frete, parcelas e opções operacionais."
            collapsible
            isOpen={openSections.technical}
            onToggle={() => toggleSection('technical')}
          >
            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>
                    Limite de valor para pagamentos online
                    <i
                      id="onlinePaymentsLimit"
                      className="las la-info-circle font-size-18 ml-1"
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target="onlinePaymentsLimit"
                    >
                      Valor máximo de um pagamento online para essa loja, Digite
                      0 para ilimitado.
                    </UncontrolledTooltip>
                  </Label>
                  <IntlCurrencyInput
                    currency="BRL"
                    onChange={(_, limit) => {
                      setOnlinePaymentLimit(limit);
                    }}
                    name="online_payments_value_limit"
                    id="online_payments_value_limit"
                    placeholder="Limite de pagamento online"
                    className="w-100 form-control"
                    value={onlinePaymentLimit}
                    config={{
                      locale: 'pt-BR',
                      formats: {
                        number: {
                          BRL: {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        },
                      },
                    }}
                  />
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Saldo mínimo no serviço de entrega</Label>
                  <i
                    id="stdMnimumBalance"
                    className="las la-info-circle font-size-18 ml-1"
                  />
                  <UncontrolledTooltip
                    placement="top"
                    target="stdMnimumBalance"
                  >
                    Mínimo de R$ 50,00
                  </UncontrolledTooltip>
                  <IntlCurrencyInput
                    currency="BRL"
                    onChange={(_, minimum) => {
                      setMinimumDeliveryBalance(minimum);
                    }}
                    value={minimumDeliveryBalance}
                    name="minimum_delivery_balance"
                    id="minimum_delivery_balance"
                    placeholder="Saldo mínimo no serviço de entrega"
                    className="w-100 form-control"
                    config={{
                      locale: 'pt-BR',
                      formats: {
                        number: {
                          BRL: {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        },
                      },
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>Quantidade máxima de parcelas</Label>
                  <i
                    id="stdMaximumInstallments"
                    className="las la-info-circle font-size-18 ml-1"
                  />
                  <UncontrolledTooltip
                    placement="top"
                    target="stdMaximumInstallments"
                  >
                    O campo “Quantidade máxima de parcelas” funcionará como um
                    limitador de parcelas, no máximo até X parcelas, havendo ou
                    não um valor de parcela mínima.
                  </UncontrolledTooltip>
                  <Input
                    name="maximum_installments"
                    placeholder="Quantidade máxima de parcelas"
                    type="number"
                    defaultValue={maximumInstallments}
                  />
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Valor da parcela mínima</Label>
                  <i
                    id="stdInstallmentLimit"
                    className="las la-info-circle font-size-18 ml-1"
                  />
                  <UncontrolledTooltip
                    placement="top"
                    target="stdInstallmentLimit"
                  >
                    Se este campo estiver preenchido, toda venda terá um valor
                    limite da parcela, dessa forma vai ser exibido o limite de
                    quantidade de parcelas usando esse valor como base.
                  </UncontrolledTooltip>
                  <IntlCurrencyInput
                    currency="BRL"
                    onChange={(_, installmentLimit) => {
                      setInstallmentLimitAmount(installmentLimit);
                    }}
                    value={installmentLimitAmount}
                    name="installment_limit_amount"
                    id="installment_limit_amount"
                    placeholder="Valor da parcela mínima"
                    className="w-100 form-control"
                    config={{
                      locale: 'pt-BR',
                      formats: {
                        number: {
                          BRL: {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        },
                      },
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>
                    Valor de frete padrão
                    <i
                      id="stdDefaultFreight"
                      className="las la-info-circle font-size-18 ml-1"
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target="stdDefaultFreight"
                    >
                      O valor informado nesse campo será utilizado no fluxo de
                      nova venda.
                    </UncontrolledTooltip>
                  </Label>
                  <IntlCurrencyInput
                    currency="BRL"
                    onChange={(_, freight) => {
                      setDefaultFreight(freight);
                    }}
                    name="default_freight"
                    id="default_freight"
                    placeholder="Valor de frete padrão"
                    className="w-100 form-control"
                    value={defaultFreight}
                    config={{
                      locale: 'pt-BR',
                      formats: {
                        number: {
                          BRL: {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        },
                      },
                    }}
                  />
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Distância máxima em KM para efetuar entregas</Label>
                  <Input
                    name="maximum_delivery_kilometer"
                    placeholder="Distância em KM"
                    type="number"
                    value={maximumDeliveryKilometer}
                    onChange={e => setMaximumDeliveryKilometer(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>
                    Valor mínimo da compra para isenção da taxa de frete
                    <i
                      id="stdMinFreeDelivery"
                      className="las la-info-circle font-size-18 ml-1"
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target="stdMinFreeDelivery"
                    >
                      Quando o valor dos produtos da venda atingir ou
                      ultrapassar este valor, o frete grátis fica disponível
                      para ser aplicado na venda. Digite 0 para desativar.
                    </UncontrolledTooltip>
                  </Label>
                  <IntlCurrencyInput
                    currency="BRL"
                    onChange={(_, minValue) => {
                      setMinimumValueForFreeDelivery(minValue);
                    }}
                    name="minimum_value_for_free_delivery"
                    id="minimum_value_for_free_delivery"
                    placeholder="Valor mínimo para frete grátis"
                    className="w-100 form-control"
                    value={minimumValueForFreeDelivery}
                    config={{
                      locale: 'pt-BR',
                      formats: {
                        number: {
                          BRL: {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        },
                      },
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0 mt-2">
              <Col lg={6} className="px-0 pr-lg-2">
                <div className="switch-field d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center">
                      <Label className="mb-0">Frete automático</Label>
                      <i
                        id="automaticFreightTip"
                        className="las la-info-circle font-size-18 ml-2"
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target="automaticFreightTip"
                      >
                        Quando ativado, o valor cobrado do cliente será igual ao
                        custo de entrega retornado e o campo de frete ficará
                        bloqueado na venda.
                      </UncontrolledTooltip>
                    </div>
                  </div>
                  <CustomInput
                    type="switch"
                    id="useAutomaticFreightSwitch"
                    name="use_automatic_freight"
                    label={useAutomaticFreight ? 'Ativado' : 'Desativado'}
                    checked={useAutomaticFreight}
                    className="text-primary"
                    onChange={() => setUseAutomaticFreight(v => !v)}
                  />
                </div>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <div className="switch-field d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <Label className="mb-0">
                      Info de pagamento na retirada
                    </Label>
                    <i
                      id="showPaymentInfoOnPickupTooltip"
                      className="las la-info-circle font-size-18 ml-2"
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target="showPaymentInfoOnPickupTooltip"
                    >
                      Quando ativado, as informações de pagamento serão exibidas
                      após o usuário informar o código de retirada no pedido.
                    </UncontrolledTooltip>
                  </div>
                  <CustomInput
                    type="switch"
                    id="showPaymentInfoOnPickupSwitch"
                    name="hide_payment_data"
                    label={hidePaymentData ? 'Ativado' : 'Desativado'}
                    checked={hidePaymentData}
                    className="text-primary"
                    onChange={() => setHidePaymentData(v => !v)}
                  />
                </div>
              </Col>
            </Row>

            <Row className="w-100 mx-0 mt-3">
              <Col lg={6} className="px-0 pr-lg-2">
                <div className="switch-field d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center">
                      <Label className="mb-0">
                        Fluxo de Venda por Whatsapp
                      </Label>
                      <i
                        id="automaticFreightTipWhatsapp"
                        className="las la-info-circle font-size-18 ml-2"
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target="automaticFreightTipWhatsapp"
                      >
                        Quando ativado, todo fluxo da venda será via WhatsaApp,
                        O Viddy vai enviar todas as notificações automaticamente
                        para o cliente da venda.
                      </UncontrolledTooltip>
                    </div>
                  </div>
                  <CustomInput
                    type="switch"
                    id="whatsappFlowEnabledSwitch"
                    name="whatsapp_flow_enabled"
                    label={whatsappFlowEnabled ? 'Ativado' : 'Desativado'}
                    checked={whatsappFlowEnabled}
                    className="text-primary"
                    onChange={() => setWhatsappFlowEnabled(v => !v)}
                  />
                </div>
              </Col>
            </Row>
          </SectionCard>

          <SectionCard
            title="Roteador de Entregas"
            subtitle="Seleção automática de transportadora por prioridade, com rebusca e incentivo"
            collapsible
            isOpen={openSections.deliveryRouter}
            onToggle={() => toggleSection('deliveryRouter')}
          >
            <Row className="w-100 mb-2 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <div className="switch-field d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <Label className="mb-0">Ativar Roteador de Entregas</Label>
                    <i
                      id="deliveryRouterTip"
                      className="las la-info-circle font-size-18 ml-2"
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target="deliveryRouterTip"
                    >
                      Quando ativado, o vendedor não escolhe a transportadora na
                      venda. O Viddy aciona os parceiros na ordem de prioridade
                      definida abaixo e, se ninguém aceitar em 10 minutos, refaz
                      a busca com incentivo antes de passar ao próximo.
                    </UncontrolledTooltip>
                  </div>
                  <CustomInput
                    type="switch"
                    id="deliveryRouterEnabledSwitch"
                    name="delivery_router_enabled"
                    label={deliveryRouterEnabled ? 'Ativado' : 'Desativado'}
                    checked={deliveryRouterEnabled}
                    className="text-primary"
                    onChange={() => setDeliveryRouterEnabled(v => !v)}
                  />
                </div>
              </Col>
            </Row>

            <Collapse isOpen={deliveryRouterEnabled}>
              <SubSectionBlock
                title="Transportadoras e ordem de prioridade"
                hint="Arraste as linhas para definir a ordem. A transportadora no topo é acionada primeiro; se não encontrar entregador, o sistema avança para a próxima."
              >
                {loadingOperators ? (
                  <div className="text-muted font-size-14 py-3">
                    Carregando transportadoras...
                  </div>
                ) : (
                  <DeliveryOperatorsList
                    operators={deliveryOperators}
                    onChange={setDeliveryOperators}
                    vehicleLabels={vehicleLabels}
                    incentiveStep={deliveryRouterIncentiveStep}
                  />
                )}
              </SubSectionBlock>
              <SubSectionBlock
                title="Incentivo e notificações"
                hint="O incentivo é somado a cada rebusca no mesmo parceiro e volta a zero quando o roteador troca de parceiro."
              >
                <Row className="w-100 mx-0">
                  <Col lg={6} className="px-0 pr-lg-2">
                    <FormGroup>
                      <Label>
                        Valor do incentivo por rebusca
                        <i
                          id="deliveryRouterIncentiveTip"
                          className="las la-info-circle font-size-18 ml-1"
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="deliveryRouterIncentiveTip"
                        >
                          Na 1ª rebusca soma este valor; na 2ª, o dobro. Deixe
                          em zero para apenas cancelar e refazer a busca, sem
                          incentivo.
                        </UncontrolledTooltip>
                      </Label>
                      <IntlCurrencyInput
                        currency="BRL"
                        onChange={(_, value) => {
                          setDeliveryRouterIncentiveStep(value);
                        }}
                        value={deliveryRouterIncentiveStep}
                        name="delivery_router_incentive_step"
                        id="delivery_router_incentive_step"
                        placeholder="Valor do incentivo"
                        className="w-100 form-control"
                        config={{
                          locale: 'pt-BR',
                          formats: {
                            number: {
                              BRL: {
                                style: 'currency',
                                currency: 'BRL',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            },
                          },
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="px-0 pl-lg-2">
                    <FormGroup>
                      <Label>
                        Quem avisar quando não houver entregador
                        <i
                          id="deliveryRouterEmailTip"
                          className="las la-info-circle font-size-18 ml-1"
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="deliveryRouterEmailTip"
                        >
                          E-mail que recebe o aviso quando todas as
                          transportadoras forem tentadas sem sucesso e o pedido
                          precisar de intervenção manual.
                        </UncontrolledTooltip>
                      </Label>
                      <Input
                        name="delivery_router_ops_email"
                        placeholder="E-mail"
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </SubSectionBlock>
            </Collapse>
          </SectionCard>

          <SectionCard
            title="Integrações e Recursos Adicionais"
            subtitle="CRM Bônus, Uber e iFood"
            collapsible
            isOpen={openSections.integrations}
            onToggle={() => toggleSection('integrations')}
          >
            {/*
            <SubSectionBlock
              title="Pix Parcelado"
              hint="Escolha do gateway BNPL e dados adicionais."
            >
              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <Label>Gateway de pagamento</Label>

                  <FormGroup>
                    <InputSelect
                      name="bnpl_payment_gateway"
                      className="common-select"
                      options={bnplGatewayOptions}
                      onChange={selectedOption => {
                        setSelectedBnplGateway(selectedOption);
                      }}
                      value={selectedBnplGateway || ''}
                      placeholder="Gateway switch"
                      singlePath
                    />
                  </FormGroup>
                </Col>
              </Row>

              {selectedBnplGateway.value === 'koin' && (
                <>
                  <Row className="w-100 mx-0 font-size-20 font-weight-700 text-gray-700 my-4">
                    Koin
                  </Row>
                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      {hasKoinCredentialsConfigured && !editingCredentials.koin ? (
                        renderConfiguredCredentialsNotice({
                          title: 'Credencial configurada',
                          description:
                            'A chave privada da Koin já está armazenada. Clique abaixo para substituir o valor.',
                          onEdit: () => enableCredentialEditing('koin'),
                        })
                      ) : (
                        <FormGroup>
                          <Label>Chave Privada</Label>
                          <Input
                            name="koin_private_key"
                            placeholder="Chave Privada"
                            type="text"
                          />
                        </FormGroup>
                      )}
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label>Número da Conta</Label>
                        <Input
                          name="koin_account"
                          placeholder="Número da Conta"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pr-lg-2">
                      <FormGroup>
                        <Label>Identificador da Conta</Label>
                        <Input
                          name="koin_store_code"
                          placeholder="Identificador da Conta"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </>
              )}
            </SubSectionBlock>
            */}

            <SubSectionBlock
              title="CRM Bônus"
              hint="Identificadores usados na integração com o CRM Bônus."
            >
              {hasCrmBonusCredentialsConfigured &&
              !editingCredentials.crmbonus ? (
                <Row className="w-100 mx-0 mb-3">
                  <Col xs={12} className="px-0">
                    {renderConfiguredCredentialsNotice({
                      title: 'Credenciais configuradas',
                      description:
                        'As credenciais do CRM Bônus já estão armazenadas. Clique abaixo para substituir Código da Empresa e ID da Loja.',
                      onEdit: () => enableCredentialEditing('crmbonus'),
                    })}
                  </Col>
                </Row>
              ) : (
                <Row className="w-100 mx-0">
                  <Col lg={6} className="px-0 pr-lg-2">
                    <FormGroup>
                      <Label>Código da Empresa</Label>
                      <Input
                        name="crmbonus_company_code"
                        placeholder="Código da Empresa"
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} className="px-0 pl-lg-2">
                    <FormGroup>
                      <Label>ID da Loja</Label>
                      <Input
                        name="crmbonus_store_id"
                        placeholder="ID da Loja"
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </SubSectionBlock>

            {/*
            <SubSectionBlock
              title="Informações Frota Alternativa"
              hint="Dados de Mapp e notificações."
            >
              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Credenciamento (Mapp)</Label>
                    <Input
                      name="mapp_client"
                      placeholder="Código do Cliente"
                      type="text"
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <FormGroup>
                    <Label>E-mail para notificações</Label>
                    <Input
                      name="email_delivery_notification"
                      placeholder="E-mail da loja"
                      type="text"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row className="w-100 mx-0 align-items-end">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>URL de Notificações (Webhook)</Label>
                    <Input
                      name="url_delivery_notification"
                      placeholder="URL para consulta"
                      type="text"
                      disabled
                      value={mappLink}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <FormGroup>
                    <Button
                      color="primary"
                      size="lg"
                      type="button"
                      className="new-layout-btn new-layout-btn-transparent main d-flex align-items-center"
                      onClick={() => {
                        copyStringToClipboard(mappLink, 'URL copiado!');
                      }}
                    >
                      <i className="las la-copy" />
                      Copiar URL
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </SubSectionBlock>
            */}

            <SubSectionBlock
              title="iFood Envios"
              hint="Merchant ID da loja no iFood Envios (ponto de coleta). Sem ele a loja não cota nem aciona entregas pelo iFood."
            >
              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Merchant ID</Label>
                    <Input
                      name="ifood_merchant_id"
                      placeholder="Merchant ID da Loja no iFood Envios"
                      type="text"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </SubSectionBlock>

            <SubSectionBlock
              title="Uber"
              hint="Dados utilizados pela integração com a Uber."
              className="mb-0"
            >
              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Customer ID</Label>
                    <Input
                      name="uber_customer_id"
                      placeholder="Customer ID da Loja na Uber"
                      type="text"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </SubSectionBlock>
          </SectionCard>

          <Row className="w-100 mx-0 my-4">
            <Col align="end" className="px-0">
              <Button
                className="new-layout-btn main"
                color="primary"
                size="lg"
                type="submit"
              >
                Salvar Alterações
              </Button>
            </Col>
          </Row>
          <Modal
            isOpen={showRemoveHubModal}
            toggle={() => setShowRemoveHubModal(false)}
            centered
          >
            <ModalHeader toggle={() => setShowRemoveHubModal(false)}>
              Remover Integração Pagar.me Hub
            </ModalHeader>
            <ModalBody>
              <p className="mb-2">
                Tem certeza que deseja remover a integração com o Pagar.me Hub?
              </p>
              <p className="text-muted font-size-13 mb-2">
                Para concluir, clique no botão{' '}
                <strong>
                  Salvar
                  {selectedGateway?.value === 'pagarme_hub'
                    ? ''
                    : ' Alterações'}
                </strong>{' '}
                no final da página.
              </p>
              <p>Enquanto não salvar, a integração permanece ativa.</p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                className="new-layout-btn main"
                onClick={() => setShowRemoveHubModal(false)}
                outline
              >
                Cancelar
              </Button>
              <Button
                color="danger"
                className="new-layout-btn main"
                onClick={() => {
                  setPagarmeHub(null);
                  setShowRemoveHubModal(false);
                  toast.info(
                    'Integração marcada para remoção. Clique em Salvar para confirmar.'
                  );
                }}
              >
                Remover Integração
              </Button>
            </ModalFooter>
          </Modal>
        </Form>
      </Container>
    </div>
  );
};

export default EditStore;
